import { createEvent } from 'ics';
import { ScheduledMatch } from '@/types/scheduledMatch';

// Device detection utilities
export const isIOS = (): boolean => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

export const isSafari = (): boolean => {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

export const isInStandaloneMode = (): boolean => {
  return window.matchMedia('(display-mode: standalone)').matches || 
         (navigator as any).standalone === true;
};

export interface CalendarEventData {
  title: string;
  description: string;
  location?: string;
  start: [number, number, number, number, number]; // Year, Month, Day, Hour, Minute
  startInputType: 'local' | 'utc';
  startOutputType: 'local' | 'utc';
  end: [number, number, number, number, number];
  endInputType: 'local' | 'utc';
  endOutputType: 'local' | 'utc';
  uid: string;
  categories: string[];
  alarms?: Array<{
    action: 'display';
    description: string;
    trigger: {
      hours?: number;
      minutes?: number;
      before: true;
    };
  }>;
}

export const generateCalendarEvent = (match: ScheduledMatch): CalendarEventData => {
  const matchDate = new Date(match.match_date);
  
  // Create teams description
  const team1 = [
    match.team1_player1_profile?.full_name,
    match.team1_player2_profile?.full_name
  ].filter(Boolean).join(' & ');
  
  const team2 = [
    match.team2_player1_profile?.full_name,
    match.team2_player2_profile?.full_name
  ].filter(Boolean).join(' & ');

  const title = `Padel Match: ${team1} vs ${team2}`;
  
  const description = [
    `Group: ${match.group_name || 'Unknown Group'}`,
    match.poll_title ? `Poll: ${match.poll_title}` : '',
    '',
    'Players:',
    `Team 1: ${team1}`,
    `Team 2: ${team2}`,
    '',
    'Don\'t forget to capture your scores after the match!',
    '',
    'Generated by LuvALL'
  ].filter(Boolean).join('\n');

  // Calculate end time (2 hours after start)
  const endDate = new Date(matchDate.getTime() + 2 * 60 * 60 * 1000);

  // IMPORTANT: Database now stores proper UTC times (e.g., 9 AM CET is stored as 8 AM UTC)
  // We use UTC components and tell the ICS library to treat them as UTC
  // Calendar apps will then convert to the user's local timezone for display
  return {
    title,
    description,
    location: match.club?.club_name || undefined,
    start: [
      matchDate.getUTCFullYear(),
      matchDate.getUTCMonth() + 1, // ICS months are 1-based
      matchDate.getUTCDate(),
      matchDate.getUTCHours(),
      matchDate.getUTCMinutes()
    ],
    startInputType: 'utc' as const, // Input is in UTC
    startOutputType: 'utc' as const, // Output as UTC (calendar apps will convert to local)
    end: [
      endDate.getUTCFullYear(),
      endDate.getUTCMonth() + 1,
      endDate.getUTCDate(),
      endDate.getUTCHours(),
      endDate.getUTCMinutes()
    ],
    endInputType: 'utc' as const,
    endOutputType: 'utc' as const,
    uid: `scheduled-match-${match.id}@luvall.app`,
    categories: ['Sports', 'Padel'],
    alarms: [
      {
        action: 'display' as const,
        description: 'Padel match starting in 15 minutes',
        trigger: { minutes: 15, before: true }
      },
      {
        action: 'display' as const,
        description: 'Padel match starting in 1 hour',
        trigger: { hours: 1, before: true }
      }
    ]
  };
};

export const createICSFile = (match: ScheduledMatch): Promise<string> => {
  return new Promise((resolve, reject) => {
    const eventData = generateCalendarEvent(match);
    
    createEvent(eventData, (error, value) => {
      if (error) {
        console.error('Error creating ICS file:', error);
        reject(error);
      } else {
        resolve(value);
      }
    });
  });
};

export const createBulkICSFile = (matches: ScheduledMatch[]): Promise<string> => {
  return new Promise((resolve, reject) => {
    try {
      const events = matches.map(generateCalendarEvent);
      
      // For bulk events, we need to create each event separately and combine
      const promises = events.map(event => 
        new Promise<string>((res, rej) => {
          createEvent(event, (error, value) => {
            if (error) rej(error);
            else res(value);
          });
        })
      );

      Promise.all(promises)
        .then(icsEvents => {
          // Combine all events into one calendar
          const header = 'BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//LuvALL//Scheduled Matches//EN\nCALSCALE:GREGORIAN\nMETHOD:PUBLISH';
          const footer = 'END:VCALENDAR';
          
          // Extract event content (without calendar wrapper) from each ICS
          const eventContents = icsEvents.map(ics => {
            const lines = ics.split('\n');
            const startIndex = lines.findIndex(line => line.startsWith('BEGIN:VEVENT'));
            const endIndex = lines.findIndex(line => line.startsWith('END:VEVENT'));
            return lines.slice(startIndex, endIndex + 1).join('\n');
          });

          const combinedICS = [header, ...eventContents, footer].join('\n');
          resolve(combinedICS);
        })
        .catch(reject);
    } catch (error) {
      reject(error);
    }
  });
};

// iOS-optimized calendar download with multiple fallback methods
export const downloadICSFile = (icsContent: string, filename: string = 'padel-match.ics'): { success: boolean; method: string } => {
  console.log('[Calendar] Attempting download on:', { 
    isIOS: isIOS(), 
    isSafari: isSafari(), 
    isStandalone: isInStandaloneMode(),
    hasNavigatorShare: !!navigator.share
  });

  try {
    if (isIOS()) {
      // On iOS, try data URL first (works better than blob URLs)
      const dataUrl = `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`;
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = filename;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('[Calendar] iOS data URL download attempted');
      return { success: true, method: 'ios-data-url' };
    } else {
      // Standard blob download for other platforms
      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      
      console.log('[Calendar] Standard blob download completed');
      return { success: true, method: 'blob-download' };
    }
  } catch (error) {
    console.error('[Calendar] Download failed:', error);
    return { success: false, method: 'failed' };
  }
};

// Enhanced sharing for iOS with better error handling and fallbacks
export const shareCalendarEvent = async (icsContent: string, match: ScheduledMatch): Promise<{ success: boolean; method: string }> => {
  console.log('[Calendar] Attempting to share calendar event on iOS');
  
  if (navigator.share && navigator.canShare) {
    try {
      const blob = new Blob([icsContent], { type: 'text/calendar' });
      const file = new File([blob], `padel-match-${match.id}.ics`, { type: 'text/calendar' });
      
      // Check if files can be shared
      const canShare = navigator.canShare({ files: [file] });
      console.log('[Calendar] Can share files:', canShare);
      
      if (canShare) {
        await navigator.share({
          title: `Padel Match - ${new Date(match.match_date).toLocaleDateString()}`,
          text: `Scheduled padel match in ${match.group_name}`,
          files: [file]
        });
        
        console.log('[Calendar] Successfully shared via Web Share API');
        return { success: true, method: 'web-share-api' };
      }
    } catch (error) {
      console.error('[Calendar] Web Share API failed:', error);
      // Don't return here, fall through to other methods
    }
  }
  
  // Fallback: Try to open the calendar data in a new window/tab for iOS
  if (isIOS()) {
    try {
      const dataUrl = `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`;
      const newWindow = window.open(dataUrl, '_blank');
      
      if (newWindow) {
        console.log('[Calendar] Opened calendar data in new window for iOS');
        return { success: true, method: 'ios-new-window' };
      }
    } catch (error) {
      console.error('[Calendar] iOS new window fallback failed:', error);
    }
  }
  
  console.log('[Calendar] All sharing methods failed');
  return { success: false, method: 'no-share-support' };
};

// Comprehensive calendar integration function optimized for iOS
export const addToCalendarOptimized = async (icsContent: string, match: ScheduledMatch): Promise<{ success: boolean; method: string; message: string }> => {
  console.log('[Calendar] Starting iOS-optimized calendar integration');
  
  try {
    if (isIOS()) {
      // Priority 1: Web Share API (best for iOS)
      const shareResult = await shareCalendarEvent(icsContent, match);
      if (shareResult.success) {
        return {
          success: true,
          method: shareResult.method,
          message: 'Calendar event shared successfully! Tap to add to your calendar.'
        };
      }
      
      // Priority 2: Data URL download
      const downloadResult = downloadICSFile(icsContent, `padel-match-${match.id}.ics`);
      if (downloadResult.success) {
        return {
          success: true,
          method: downloadResult.method,
          message: 'Calendar file ready! Check your Downloads folder and tap to add to calendar.'
        };
      }
      
      return {
        success: false,
        method: 'ios-all-failed',
        message: 'Unable to add to calendar automatically. Please save the event details manually.'
      };
    } else {
      // Non-iOS: Standard blob download
      const downloadResult = downloadICSFile(icsContent, `padel-match-${match.id}.ics`);
      return {
        success: downloadResult.success,
        method: downloadResult.method,
        message: downloadResult.success 
          ? 'Calendar event downloaded successfully!'
          : 'Download failed. Please try again.'
      };
    }
  } catch (error) {
    console.error('[Calendar] All calendar integration methods failed:', error);
    return {
      success: false,
      method: 'error',
      message: 'Calendar integration failed. Please add the match to your calendar manually.'
    };
  }
};

export const getCalendarButtonText = (preference: string): string => {
  switch (preference) {
    case 'auto_download':
      return 'Added to Calendar';
    case 'disabled':
      return 'Calendar Disabled';
    default:
      return 'Add to Calendar';
  }
};

export const shouldAutoGenerateCalendar = (preference: string): boolean => {
  return preference === 'auto_download';
};